export enum Error {
  // The record we queried could not be found.
  NotFound = "not_found",

  // The action that we want to perform conflicts with the existing state.
  Conflict = "conflict",

  // The server knows who we are, but we are not allowed to do this.
  Forbidden = "forbidden",

  // We were unable to proof to the server who we are.
  Unauthorized = "unauthorized",

  Network = "network_error",

  BadRequest = "bad_request",

  // Either 500 Server Error or some other response code that we aren't
  // handling.
  Bug = "bug",
}

export type ParcialUser = {
  user_id: string;
  email: string;
  given_name: string;
  middle_names: string;
  family_name: string;
};

export type User = ParcialUser & {
  created_at: string;
};

export type Organization = {
  organization_id: string;
  name: string;
};

export type OrganizationWithUserContext = Organization & {
  role: OrganizationMemberRole;
};

export enum OrganizationMemberRole {
  Regular = 20,
  Admin = 80,
  Root = 100,
}

export type AlarmTemplate = {
  // Only "title" is properly types as we don't care about the rest yet.
  appearance_ios: any;
  appearance_android: any;
  title: string;
  manual_sections: any;
};

export type AlarmGroup = {
  alarm_group_id: string;
  organization_id: string;
  template: AlarmTemplate;
  users: ParcialUser[];
  assembly_points: { [k: string]: string };
  custom_assembly_point_allowed: boolean;
};

export type Invite = {
  invite_id: string;
  organization: Organization;
  role: OrganizationMemberRole;
  expires_at: string;
  created_at: string;
};

export type InviteSummary = {
  invitee_email: string;
  invitee_role: OrganizationMemberRole;
  org_name_invited_to: string;
  invitation_expires_at: string;
  org_id_invited_to: string;
  invitee_mb_user_id: string;
  invite_id: string;
};

export type API = {
  auth: {
    login(email: string, password: string): Promise<null>;
    logout(): Promise<null>;
    forgot(email: string): Promise<null>;
    reset(requestID: string, token: string, password: string): Promise<null>;
  };
  users: {
    me: {
      get(): Promise<User>;
      organizations(): Promise<OrganizationWithUserContext[]>;

      invites: {
        get(): Promise<InviteSummary[]>;
        id(uuid: string): {
          get(): Promise<Invite>;
          post(): Promise<null>;
          delete(): Promise<null>;
        };
      };

    };
    post(
      requestID: string,
      token: string,
      givenName: string,
      middleNames: string,
      familyName: string,
      password: string
    ): Promise<null>;
  };
  organizations: {
    id(uuid: string): {
      root: {
        createOrganization(name: string): Promise<null>;
        addUserToOrganization(
          organizationID: string,
          email: string,
          role: OrganizationMemberRole
        ): Promise<null>;
      };
      alarmgroups: {
        get(): Promise<AlarmGroup[]>;
        id(uuid: string): {
          members: {
            id(uuid: string): {
              put(): Promise<null>;
              delete(): Promise<null>;
            };
          };
          assignable(): Promise<ParcialUser[]>;
        };
      };
      organizationusers: {
        get(): Promise<User[]>;
      };
      invites: {
        get(): Promise<InviteSummary[]>;
        post(
          email: string,
          givenName: string,
          middleNames: string,
          familyName: string
        ): Promise<null>;
		delete(
			inviteID: string
		): Promise<null>
      };
    };
  };

  // The callback given to onUnauthorized is called whenever an endpoint
  // returns 401 Unauthorized.
  onUnauthorized: (callback: () => void) => void;
};

// Makes requests to the MB API server. This type is dependency injected into
// the API functions, and can therefore be mocked for unit tests.
export type FetchAPIFunc = (
  endpoint: string,
  init: RequestInit | undefined
) => Promise<Response>;
